import './NpcArchetypesDrawer.css';
import { useRef, useState } from 'react';
import { DiceRollTumble } from '../atmosphere/DiceRollTumble';
import type {
  ArchetypeManifestEntry,
  ArchetypeCategory,
} from '../../services/host/NpcArchetypesServiceModule';
import type {
  NpcArchetypesLibraryState,
  NpcArchetypesLibraryTab,
} from '../../hooks/useNpcArchetypesLibrary';

/**
 * NpcArchetypesDrawer — 2026-06-08 Phase 6 for Core Rulebook §28 archetype
 * generator tables. Surfaces the canonical 8 archetypes (already-authored
 * seed) across 3 narrative tabs Allies/Enemies/Neutrals.
 *
 * Per-archetype detail: description + canonical examples + characteristic
 * default rank + 2d6 "Roll me one" generator tool (Workstream C).
 *
 * Followup 3 (dice-box cinematic upgrade):
 *   First Roll: mounts DiceRollTumble with rollMode='damage' (2d6 notation).
 *     - onResult callback receives the dice-box total as the characteristicsRoll.
 *     - A second pure-JS roll2d6() produces skillsRoll immediately, so both
 *       values are ready when onComplete fires and the result panel renders.
 *   Re-roll: instant pure-JS (no cinematic) — iterative use case.
 *
 * Design choice — single 2d6 cinematic vs two sequential:
 *   Single wins. Two cinematics would be UX-jarring (double mount/unmount).
 *   Using dice-box 2d6 for the charRoll + silent pure-JS for skillsRoll
 *   is semantically pure enough (GM is rolling for NPC characteristics
 *   — the dramatic roll) while keeping the UX clean. The skills roll
 *   happens silently at the same moment; both values surface together
 *   when the cinematic settles.
 */

// ---------------------------------------------------------------------------
// 2d6 generation pure functions — exported for unit testing
// ---------------------------------------------------------------------------

export interface RolledNpcResult {
  characteristicsRoll: number;
  skillsRoll: number;
  characteristics: { STR: number; DEX: number; WIL: number; PER: number; INT: number };
  skills: Array<{ name: string; level: number }>;
  /** The rank key that was actually applied (may differ from requested if
   *  mastermind is NOT_SUPPORTED — falls back to 'default'). */
  rankApplied: string;
}

/** Clamp a value to the Bond RPG characteristic/skill range [1, 15]. */
export function clamp1to15(n: number): number {
  return Math.max(1, Math.min(15, n));
}

type RankKey = 'default' | 'hood_rookie' | 'mastermind';

/**
 * Roll one NPC from the given archetype data at the requested rank.
 *
 * `providedCharRoll` — when the dice-box cinematic path is used, the caller
 * passes the cinematic's settled total as the characteristics roll so both
 * the animated dice and the result panel reflect the same value. When
 * undefined (Re-roll / instant path), a fresh pure-JS roll2d6() is used.
 */
export function rollNpc(
  data: Record<string, unknown>,
  requestedRank: RankKey,
  providedCharRoll?: number,
): RolledNpcResult {
  const roll2d6 = (): number =>
    Math.floor(Math.random() * 6) + 1 + Math.floor(Math.random() * 6) + 1;

  // Clamp providedCharRoll to [2,12] in case dice-box returns an unexpected
  // value; pure-JS path always produces [2,12].
  const charRoll =
    providedCharRoll !== undefined
      ? Math.max(2, Math.min(12, providedCharRoll))
      : roll2d6();
  const skillRoll = roll2d6();

  // --- characteristics lookup ---
  const charTable = (data['characteristics_table_2d6'] ?? []) as Array<{
    roll: number;
    STR?: number;
    DEX?: number;
    WIL?: number;
    PER?: number;
    INT?: number;
  }>;
  const charRow = charTable.find((r) => r.roll === charRoll) ?? charTable[0] ?? { roll: 7 };

  // --- skills lookup ---
  const skillTable = (data['skills_table_2d6'] ?? []) as Array<{
    roll: number;
    skills: Array<{ name: string; level: number }>;
  }>;
  const skillRow = skillTable.find((r) => r.roll === skillRoll) ?? skillTable[0] ?? { roll: 7, skills: [] };

  // --- rank modifier resolution ---
  const rankModifiers = (data['rank_modifiers'] ?? {}) as Record<string, unknown>;

  // Determine effective rank: if mastermind is a string (NOT_SUPPORTED) fall
  // back to default when the caller asked for mastermind.
  let effectiveRank: RankKey = requestedRank;
  if (requestedRank === 'mastermind') {
    // The mastermind key can be either 'mastermind' or 'mastermind_00'
    const mmVal = rankModifiers['mastermind'] ?? rankModifiers['mastermind_00'];
    if (typeof mmVal === 'string') {
      // NOT_SUPPORTED — silently fall back to default
      effectiveRank = 'default';
    }
  }

  let charMod = 0;
  let skillMod = 0;

  if (effectiveRank === 'hood_rookie') {
    const mod = rankModifiers['hood_rookie'] ?? rankModifiers['hood'];
    if (mod && typeof mod === 'object') {
      charMod = (mod as { characteristic: number }).characteristic ?? 0;
      skillMod = (mod as { skill: number }).skill ?? 0;
    }
  } else if (effectiveRank === 'mastermind') {
    // mastermind_00 key wins over mastermind when both exist; check both
    const mod = rankModifiers['mastermind_00'] ?? rankModifiers['mastermind'];
    if (mod && typeof mod === 'object') {
      charMod = (mod as { characteristic: number }).characteristic ?? 0;
      skillMod = (mod as { skill: number }).skill ?? 0;
    }
  }

  // --- apply modifiers and clamp ---
  const characteristics = {
    STR: clamp1to15((charRow.STR ?? 7) + charMod),
    DEX: clamp1to15((charRow.DEX ?? 7) + charMod),
    WIL: clamp1to15((charRow.WIL ?? 7) + charMod),
    PER: clamp1to15((charRow.PER ?? 7) + charMod),
    INT: clamp1to15((charRow.INT ?? 7) + charMod),
  };

  const skills = (skillRow.skills ?? []).map((s) => ({
    name: s.name,
    level: clamp1to15(s.level + skillMod),
  }));

  return {
    characteristicsRoll: charRoll,
    skillsRoll: skillRoll,
    characteristics,
    skills,
    rankApplied: effectiveRank,
  };
}

// ---------------------------------------------------------------------------
// Helper: detect if mastermind rank is supported for an archetype
// ---------------------------------------------------------------------------
function isMastermindSupported(rankModifiers: Record<string, unknown>): boolean {
  const mmVal = rankModifiers['mastermind'] ?? rankModifiers['mastermind_00'];
  if (mmVal === undefined) return false;
  // String value = NOT_SUPPORTED sentinel
  return typeof mmVal !== 'string';
}

const TAB_DEFS: ReadonlyArray<{ key: NpcArchetypesLibraryTab; label: string }> = [
  { key: 'allies', label: 'Allies' },
  { key: 'enemies', label: 'Enemies' },
  { key: 'neutrals', label: 'Neutrals' },
];

const TAB_TO_CATEGORY: Record<NpcArchetypesLibraryTab, ArchetypeCategory> = {
  allies: 'allies',
  enemies: 'enemies',
  neutrals: 'neutrals',
};

export interface NpcArchetypesDrawerProps {
  archetypes: ArchetypeManifestEntry[];
  state: NpcArchetypesLibraryState;
  error?: string | null;
  activeTab: NpcArchetypesLibraryTab;
  onTabChange: (tab: NpcArchetypesLibraryTab) => void;
  selectedArchetypeId: string | null;
  selectedArchetypeData: Record<string, unknown> | null;
  selectedArchetypeState: NpcArchetypesLibraryState;
  onSelectArchetype: (id: string) => void;
  onClose?: () => void;
  testidPrefix?: string;
}

const RANK_LABELS: Record<string, string> = {
  default: 'Default (Criminal/Agent)',
  hood_rookie: 'Hood/Rookie (–)',
  mastermind: 'Mastermind (+)',
};

function ArchetypeDetail({
  data,
  testidPrefix,
}: {
  data: Record<string, unknown>;
  testidPrefix: string;
}): JSX.Element {
  const description = (data['description'] as string) ?? '';
  const examples = (data['canonical_examples'] ?? []) as string[];
  const defaultRank =
    (data['characteristics_table_default_rank'] as string) ?? '';
  const rankModifiers = (data['rank_modifiers'] ?? {}) as Record<
    string,
    unknown
  >;
  const characteristicsTable = (data['characteristics_table_2d6'] ?? []) as Array<{
    roll: number;
    STR?: number;
    DEX?: number;
    WIL?: number;
    PER?: number;
    INT?: number;
  }>;
  const skillsTable = (data['skills_table_2d6'] ?? []) as Array<unknown>;
  const descriptionPage = data['description_page'] as number | undefined;
  const tablePage = data['table_page'] as number | undefined;
  const manifestSection = data['manifest_section'] as string | undefined;
  const name = data['name'] as string | undefined;

  const mastermindEnabled = isMastermindSupported(rankModifiers);

  const [selectedRank, setSelectedRank] = useState<RankKey>('default');
  const [rollResult, setRollResult] = useState<RolledNpcResult | null>(null);
  // Cinematic state: true while DiceRollTumble is mounted for the first Roll.
  // Cleared by handleCinematicComplete. Re-roll bypasses cinematic entirely.
  const [cinematicPending, setCinematicPending] = useState(false);
  // Ref bridge: onResult fires before onComplete; we compute the NPC here so
  // onComplete can set it atomically without triggering an extra render.
  const pendingResultRef = useRef<RolledNpcResult | null>(null);

  /** First Roll button: mount the dice-box cinematic. */
  function handleFirstRoll() {
    pendingResultRef.current = null;
    setCinematicPending(true);
  }

  /**
   * Fires from DiceRollTumble onResult (dice settle).
   * Computes the NPC using the dice-box total as charRoll; stores in ref.
   * onComplete fires next and sets the result state.
   *
   * Signature matches DiceRollTumbleProps['onResult'] exactly — the
   * subcomponent passes {mode, total, breakdown} per its interface at
   * DiceRollTumble.tsx; we only use `total`, but accepting the full
   * shape avoids TypeScript narrowing at the call site (per reviewer
   * concern, F3 reviewer 2026-06-08).
   */
  function handleCinematicResult(result: { mode: string; total: number; breakdown: number[] }) {
    pendingResultRef.current = rollNpc(data, selectedRank, result.total);
  }

  /** Fires from DiceRollTumble onComplete (user dismisses). Sets result. */
  function handleCinematicComplete() {
    setCinematicPending(false);
    if (pendingResultRef.current !== null) {
      setRollResult(pendingResultRef.current);
      pendingResultRef.current = null;
    }
  }

  /**
   * Re-roll button: instant pure-JS (no cinematic).
   *
   * 2026-06-08 reviewer-correction (F3 reviewer Concern 3): defensive
   * guard against the (currently unreachable) race where a Re-roll
   * click could fire while the dice-box cinematic is still pending.
   *
   * Today this race CANNOT happen because:
   *   - The Re-roll button is mounted inside `{rollResult !== null && (…)}`
   *   - rollResult is set ONLY by handleCinematicComplete AFTER it has
   *     called setCinematicPending(false) — so during cinematicPending,
   *     rollResult is null, the Re-roll button is unmounted, and click
   *     cannot fire
   *
   * The guard below is defense-in-depth: if a future refactor changes
   * the gating (e.g. moves setRollResult before setCinematicPending(false),
   * or shows Re-roll during the cinematic for some UX reason), the guard
   * still prevents corrupting cinematicPending state. The cost is a
   * single early-return branch on a hot path that's already cheap.
   */
  function handleReRoll() {
    if (cinematicPending) return;
    setRollResult(rollNpc(data, selectedRank));
  }

  return (
    <div
      className="br-archetype-detail"
      data-testid={`${testidPrefix}-detail`}
    >
      <div className="br-archetype-detail-header">
        <div className="br-archetype-detail-name br-display">{name}</div>
        <div className="br-archetype-detail-citation br-label">
          Core Rulebook §{manifestSection} · prose p.{descriptionPage} ·
          tables p.{tablePage}
        </div>
      </div>

      <div className="br-archetype-detail-section">
        <div className="br-archetype-detail-section-label">Description</div>
        <p className="br-archetype-detail-section-body">{description}</p>
      </div>

      {examples.length > 0 && (
        <div className="br-archetype-detail-section">
          <div className="br-archetype-detail-section-label">
            Canonical examples
          </div>
          <ul className="br-archetype-detail-examples">
            {examples.map((ex) => (
              <li key={ex} className="br-archetype-detail-example">
                {ex}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="br-archetype-detail-section">
        <div className="br-archetype-detail-section-label">
          Default rank · rank modifiers
        </div>
        <div className="br-archetype-detail-rank">
          <span className="br-archetype-detail-rank-default br-label">
            {defaultRank}
          </span>
          <span className="br-archetype-detail-rank-modifiers br-label">
            {Object.entries(rankModifiers)
              .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
              .join(' · ')}
          </span>
        </div>
      </div>

      {/* ── Roll me one ── */}
      <div className="br-archetype-detail-section br-archetype-roll-section">
        <div className="br-archetype-detail-section-label">
          Generate NPC · 2d6 roll
        </div>

        {/* Rank picker */}
        <div
          className="br-archetype-rank-picker"
          role="radiogroup"
          aria-label="NPC rank"
        >
          {(['default', 'hood_rookie', 'mastermind'] as RankKey[]).map(
            (rank) => {
              const disabled = rank === 'mastermind' && !mastermindEnabled;
              return (
                <label
                  key={rank}
                  className={`br-archetype-rank-radio${disabled ? ' br-archetype-rank-radio--disabled' : ''}`}
                  title={
                    disabled
                      ? (rankModifiers['mastermind'] as string | undefined) ??
                        'Not supported for this archetype'
                      : undefined
                  }
                >
                  <input
                    type="radio"
                    name={`${testidPrefix}-rank`}
                    value={rank}
                    checked={selectedRank === rank}
                    disabled={disabled}
                    data-testid={`${testidPrefix}-rank-${rank}`}
                    onChange={() => {
                      if (!disabled) setSelectedRank(rank);
                    }}
                  />
                  <span className="br-label">{RANK_LABELS[rank]}</span>
                </label>
              );
            },
          )}
        </div>

        <button
          type="button"
          className="br-archetype-roll-btn"
          data-testid={`${testidPrefix}-roll-btn`}
          disabled={cinematicPending}
          onClick={handleFirstRoll}
        >
          🎲 Roll one
        </button>

        {/* Dice-box cinematic — mounts on first Roll, unmounts when dismissed */}
        {cinematicPending && (
          <DiceRollTumble
            testidPrefix="br-archetype-roll-tumble"
            rollMode="damage"
            onResult={handleCinematicResult}
            onComplete={handleCinematicComplete}
          />
        )}

        {/* Result panel */}
        {rollResult !== null && (
          <div
            className="br-archetype-roll-result"
            data-testid={`${testidPrefix}-result`}
          >
            <div className="br-archetype-roll-result-header">
              <span
                className="br-archetype-roll-result-rank br-label"
                data-testid={`${testidPrefix}-result-rank`}
              >
                {rollResult.rankApplied}
              </span>
              <span className="br-archetype-roll-result-dice br-label">
                chars 2d6={rollResult.characteristicsRoll} · skills
                2d6={rollResult.skillsRoll}
              </span>
            </div>

            <div className="br-archetype-roll-result-chars">
              {(['STR', 'DEX', 'WIL', 'PER', 'INT'] as const).map((stat) => (
                <div
                  key={stat}
                  className="br-archetype-roll-result-char"
                  data-testid={`${testidPrefix}-result-${stat}`}
                >
                  <span className="br-archetype-roll-result-char-label br-label">
                    {stat}
                  </span>
                  <span className="br-archetype-roll-result-char-value br-display">
                    {rollResult.characteristics[stat]}
                  </span>
                </div>
              ))}
            </div>

            <ul
              className="br-archetype-roll-result-skills"
              data-testid={`${testidPrefix}-result-skills`}
            >
              {rollResult.skills.map((sk) => (
                <li
                  key={sk.name}
                  className="br-archetype-roll-result-skill br-label"
                >
                  {sk.name} {sk.level}
                </li>
              ))}
            </ul>

            <button
              type="button"
              className="br-archetype-roll-btn br-archetype-reroll-btn"
              data-testid={`${testidPrefix}-reroll-btn`}
              onClick={handleReRoll}
            >
              🎲 Re-roll
            </button>
          </div>
        )}
      </div>

      <div className="br-archetype-detail-section">
        <div className="br-archetype-detail-section-label">
          2d6 generation tables
        </div>
        <p className="br-archetype-detail-tables-hint br-label">
          {characteristicsTable.length} characteristics rows ·{' '}
          {skillsTable.length} skills rows — seed/canonical/npc_creation/npc_archetypes.json.
        </p>
      </div>
    </div>
  );
}

export function NpcArchetypesDrawer({
  archetypes,
  state,
  error,
  activeTab,
  onTabChange,
  selectedArchetypeId,
  selectedArchetypeData,
  selectedArchetypeState,
  onSelectArchetype,
  onClose,
  testidPrefix = 'br-npc-archetypes-library',
}: NpcArchetypesDrawerProps): JSX.Element {
  const visibleArchetypes = archetypes.filter(
    (a) => a.category === TAB_TO_CATEGORY[activeTab],
  );

  return (
    <aside
      className="br-scenario-drawer br-npc-archetypes-library-drawer"
      data-testid={testidPrefix}
      data-state={state}
    >
      <header>
        <h2 className="br-display">NPC Archetypes</h2>
        {/* 2026-06-09 Bug 1 drift fix — see CanonicalNpcsDrawer for the
         *  full rationale. Mirrors the 7 sibling drawers that already
         *  render-gate this on `onClose !== undefined`. */}
        {onClose !== undefined && (
          <button
            type="button"
            className="br-scenario-drawer-close"
            data-testid={`${testidPrefix}-close`}
            onClick={onClose}
            aria-label="Close NPC Archetypes drawer"
          >
            ✕
          </button>
        )}
      </header>

      <p className="br-label br-archetype-banner">
        Core Rulebook §28 · 8 archetypes. The GM rolls 2d6 on the chosen
        archetype's tables to generate NPC stats + skills. Click a row for
        examples + page references; full tables in the seed file.
      </p>

      <div className="br-scenario-drawer-tabs">
        {TAB_DEFS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`${testidPrefix}-tab-${t.key}`}
            data-active={t.key === activeTab ? 'true' : 'false'}
            onClick={() => onTabChange(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {state === 'loading' && (
        <div className="br-label" data-testid={`${testidPrefix}-loading`}>
          Loading archetypes…
        </div>
      )}
      {state === 'error' && (
        <div className="br-label" data-testid={`${testidPrefix}-error`}>
          {error ?? 'Failed to load archetypes.'}
        </div>
      )}
      {state === 'ready' && (
        <>
          <div data-testid={`${testidPrefix}-count`} className="br-label">
            {visibleArchetypes.length}
          </div>
          <ul
            className="br-archetype-list"
            data-testid={`${testidPrefix}-list`}
          >
            {visibleArchetypes.map((a) => (
              <li key={a.id} className="br-archetype-row-wrapper">
                <button
                  type="button"
                  className="br-archetype-row"
                  data-testid={`${testidPrefix}-row-${a.id}`}
                  data-selected={
                    a.id === selectedArchetypeId ? 'true' : 'false'
                  }
                  onClick={() => onSelectArchetype(a.id)}
                >
                  <span className="br-archetype-row-name">{a.name}</span>
                  <span className="br-archetype-row-section br-label">
                    §{a.manifest_section} · p.{a.description_page}
                  </span>
                </button>
                {a.id === selectedArchetypeId &&
                  selectedArchetypeState === 'loading' && (
                    <div
                      className="br-label"
                      data-testid={`${testidPrefix}-detail-loading`}
                    >
                      Loading detail…
                    </div>
                  )}
                {a.id === selectedArchetypeId &&
                  selectedArchetypeState === 'ready' &&
                  selectedArchetypeData && (
                    <ArchetypeDetail
                      data={selectedArchetypeData}
                      testidPrefix={testidPrefix}
                    />
                  )}
                {a.id === selectedArchetypeId &&
                  selectedArchetypeState === 'error' && (
                    <div
                      className="br-label"
                      data-testid={`${testidPrefix}-detail-error`}
                    >
                      Failed to load detail.
                    </div>
                  )}
              </li>
            ))}
          </ul>
        </>
      )}
    </aside>
  );
}
